import "./styles.css";
import {
  advancedQuestion,
  calculateResult,
  CONSENT_VERSION,
  courses,
  getAnswerLabel,
  getOccupationText,
  needsAdvancedQuestion,
  contextualQuestions,
  RULE_VERSION,
  SURVEY_VERSION,
  type SurveyAnswers,
  type SurveyQuestion,
  type SurveyResult,
} from "./shared/survey";
import { normalizeSearch, occupationFamilies, searchOccupations } from "./shared/occupations";

type View = "landing" | "survey" | "contact" | "result";
type State = {
  view: View;
  step: number;
  answers: Partial<SurveyAnswers>;
  contact: { name: string; phone: string; email: string; consent: boolean };
  result?: SurveyResult;
  submitting: boolean;
  error: string;
};

const storageKey = "funix-survey-v4-draft";
const app = document.querySelector<HTMLDivElement>("#app")!;
const trackingConsentKey = "funix-tracking-consent";
let occupationQuery = "";
let showAllOccupationFamilies = false;

const commonOccupationFamilies = ["software", "data", "accounting", "sales", "marketing", "education", "people", "health"];

const saved = (() => {
  try { return JSON.parse(sessionStorage.getItem(storageKey) || "null") as Partial<State> | null; }
  catch { return null; }
})();

const state: State = {
  view: saved?.view === "survey" || saved?.view === "contact" ? saved.view : "landing",
  step: saved?.step ?? 0,
  answers: saved?.answers ?? {},
  contact: { name: "", phone: "", email: "", consent: true },
  submitting: false,
  error: "",
};

const icon = (name: "arrow" | "check" | "spark" | "clock" | "target" | "shield") => {
  const paths = {
    arrow: '<path d="M5 12h14M13 6l6 6-6 6"/>',
    check: '<path d="m5 12 4 4L19 6"/>',
    spark: '<path d="m12 3 1.7 4.3L18 9l-4.3 1.7L12 15l-1.7-4.3L6 9l4.3-1.7L12 3Z"/><path d="m19 16 .8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8L19 16Z"/>',
    clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
    target: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4"/><path d="M12 3v3M21 12h-3"/>',
    shield: '<path d="M12 3 5 6v5c0 4.5 2.8 8 7 10 4.2-2 7-5.5 7-10V6l-7-3Z"/><path d="m9 12 2 2 4-4"/>',
  };
  return `<svg aria-hidden="true" viewBox="0 0 24 24">${paths[name]}</svg>`;
};

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[char]!);
}

function persist() {
  if (state.view === "survey" || state.view === "contact") {
    sessionStorage.setItem(storageKey, JSON.stringify({ view: state.view, step: state.step, answers: state.answers }));
  } else if (state.view === "result") sessionStorage.removeItem(storageKey);
}

function track(name: string, data: Record<string, string | number | boolean> = {}) {
  window.dispatchEvent(new CustomEvent("funix:track", { detail: { name, ...data } }));
  const w = window as typeof window & { fbq?: (...args: unknown[]) => void; clarity?: (...args: unknown[]) => void };
  if (localStorage.getItem(trackingConsentKey) !== "granted") return;
  w.clarity?.("event", name);
  if (name === "lead_submit_success") w.fbq?.("track", "Lead", {}, { eventID: String(data.submissionId ?? "") });
  else w.fbq?.("trackCustom", name, data);
}

function renderHeader() {
  return `<header class="site-header">
    <a class="brand" href="#" data-action="home" aria-label="FUNiX - Trang đầu">
      <img class="brand-logo" src="/funix-logo.png" alt="FUNiX — Learn with Mentors">
    </a>
    <span class="header-note">Khảo sát 2 phút · Nhận buổi học miễn phí cùng chuyên gia</span>
  </header>`;
}

function renderLanding() {
  return `${renderHeader()}<main id="main">
    <section class="hero">
      <div class="hero-glow one"></div><div class="hero-glow two"></div>
      <div class="hero-copy">
        <span class="eyebrow">BẢN ĐỒ ỨNG DỤNG AI CỦA RIÊNG BẠN</span>
        <h1>Dùng AI rồi nhưng không biết mình đang ở <em>mức nào</em> và nên học gì tiếp?</h1>
        <p class="hero-lead">Chỉ 2 phút để xác định đúng level hiện tại, điểm đang vướng và buổi học miễn phí phù hợp nhất với mục tiêu công việc của bạn.</p>
        <button class="primary large" data-action="start">NHẬN BUỔI HỌC MIỄN PHÍ ĐÚNG TRÌNH ĐỘ ${icon("arrow")}</button>
        <div class="trust-line"><span>${icon("clock")} Khoảng 2 phút</span><span>${icon("target")} Xếp lớp đúng level</span><span>${icon("spark")} Học cùng chuyên gia FUNiX</span></div>
      </div>
      <div class="hero-visual" aria-hidden="true">
        <div class="orbit"><div class="core">AI<span>path</span></div><i class="dot d1"></i><i class="dot d2"></i><i class="dot d3"></i></div>
        <div class="float-card top"><b>ĐÍCH ĐẾN</b><span>Agent · Automation</span></div>
        <div class="float-card bottom"><b>ĐIỂM BẮT ĐẦU</b><span>Phù hợp với bạn</span></div>
      </div>
    </section>

    <section class="intro section-pad">
      <div class="section-heading"><span class="eyebrow dark">BẮT ĐẦU TỪ ĐÚNG CHỖ</span><h2>Không cần học lại từ đầu.<br><em>Không chọn theo cảm tính.</em></h2></div>
      <div class="intro-grid">
        <div class="intro-copy"><p>Bạn có thể đã dùng ChatGPT, Claude hoặc nhiều công cụ AI khác. Giữa quá nhiều kiến thức và công cụ mới, rất khó để tự xác định:</p>
          <ul class="question-list"><li>Mình đang ứng dụng AI ở mức nào?</li><li>Vì sao kết quả vẫn chưa như mong muốn?</li><li>Nền tảng hiện tại đã đủ để làm Agent chưa?</li></ul>
        </div>
        <div class="promise-card"><div class="promise-icon">${icon("target")}</div><h3>Đích đến không quyết định điểm xuất phát</h3><p>Hệ thống dựa vào việc bạn đã làm và sản phẩm đang có để gợi ý lớp bắt đầu, sau đó vẽ đường tới mục tiêu bạn muốn.</p><button class="text-link" data-action="start">Nhận buổi học miễn phí đúng trình độ cùng chuyên gia ${icon("arrow")}</button></div>
      </div>
    </section>

    <section class="truth section-pad">
      <div class="section-heading centered"><span class="eyebrow">SỰ THẬT VỀ AI</span><h2>Dùng AI chưa chắc đã tạo ra<br><em>kết quả trong công việc</em></h2></div>
      <div class="pain-grid">
        ${[
          ["01", "Dùng nhiều nhưng vẫn phải sửa", "Kết quả thiếu ổn định hoặc vẫn cần xử lý lại gần như từ đầu."],
          ["02", "Quá nhiều công cụ để lựa chọn", "ChatGPT, Claude, Gemini, workflow, Agent… đâu mới là thứ phù hợp?"],
          ["03", "Học nhiều nhưng chưa đưa vào việc", "Biết prompt và tính năng, nhưng chưa biến thành cách làm hiệu quả hằng ngày."],
          ["04", "Muốn làm Agent nhưng thiếu nền", "Chưa rõ mình cần bổ sung kỹ năng nào trước khi tiến lên workflow hoặc Agent."],
        ].map(([n,t,d]) => `<article><span>${n}</span><h3>${t}</h3><p>${d}</p></article>`).join("")}
      </div>
    </section>

    <section class="benefits section-pad">
      <div class="benefit-copy"><span class="eyebrow dark">SAU KHI HOÀN THÀNH</span><h2>Không chỉ biết đang ở đâu.<br><em>Bạn biết bước nào tiếp theo.</em></h2><p>Định hướng được tạo từ mục tiêu, việc đã làm thật và bằng chứng bạn đang có.</p></div>
      <div class="benefit-list">
        ${[["01","Điểm xuất phát","Nhận diện chặng phù hợp với nền tảng hiện tại."],["02","Điểm đang vướng","Hiểu điều gì đang cản trở kết quả trong công việc."],["03","Buổi học cùng chuyên gia","Biết điểm bắt đầu phù hợp để tạo tiến bộ thực tế."],["04","Lộ trình phía sau","Thấy đường đi từ hiện tại đến Agent hoặc Multi-Agent."]].map(([n,t,d])=>`<div><b>${n}</b><span><strong>${t}</strong><small>${d}</small></span></div>`).join("")}
      </div>
    </section>

    <section class="funix-brand section-pad">
      <div class="funix-brand-copy"><span class="eyebrow">FUNiX ĐỒNG HÀNH CÙNG BẠN</span><h2>Để công nghệ thực sự<br><em>đi vào công việc</em></h2><p>Mô hình học trực tuyến, chú trọng thực hành và đội ngũ mentor công nghệ thực chiến giúp người học không dừng ở việc “biết dùng”, mà tiến tới “dùng được”.</p></div>
      <div class="proof"><div><b>50.000+</b><span>Học viên đã lựa chọn</span></div><div><b>5.000+</b><span>Mentor công nghệ đồng hành</span></div><div><b>200+</b><span>Doanh nghiệp đối tác — bảo chứng chất lượng<br><small>MoMo, Cốc Cốc, FPT...</small></span></div><div><b>100%</b><span>Trực tuyến, chú trọng thực hành</span></div></div>
    </section>
    <section class="final-cta"><span class="eyebrow">ƯU ĐÃI DÀNH RIÊNG CHO BẠN</span><h2>Nhận buổi học miễn phí<br><em>đúng trình độ cùng chuyên gia</em></h2><p>FUNiX dựa trên kết quả vừa hoàn thành để xác định bạn đang ở đâu, muốn đi tới đâu và nên bắt đầu với lớp nào.</p><div class="cta-benefits"><span>${icon("check")} Học đúng level, không phải bắt đầu lại từ đầu</span><span>${icon("check")} Thực hành trên bài toán công việc thực tế</span><span>${icon("check")} Mentor công nghệ hướng dẫn trong quá trình học</span><span>${icon("check")} Có lộ trình tiếp theo và cam kết đầu ra rõ ràng</span><span>${icon("check")} Học lại miễn phí đến khi thành thạo</span><span>${icon("check")} Hoàn tiền nếu không đạt đầu ra theo cam kết*</span></div><button class="primary large" data-action="start">NHẬN BUỔI HỌC MIỄN PHÍ ĐÚNG TRÌNH ĐỘ ${icon("arrow")}</button><small class="cta-note">*Áp dụng theo điều kiện và chính sách của chương trình.</small></section>
  </main>${renderFooter()}`;
}

function activeQuestions(): SurveyQuestion[] {
  const result = contextualQuestions(state.answers);
  if (needsAdvancedQuestion(state.answers)) result.push(advancedQuestion);
  return result;
}

function occupationOption(code: string, label: string, detail = "", caption = "") {
  const selected = detail ? state.answers.occupationDetail === detail : state.answers.occupation === code && !state.answers.occupationDetail;
  return `<button type="button" class="option ${selected ? "selected" : ""}" role="radio" aria-checked="${selected}" data-career="${code}" data-career-detail="${detail}"><span>${escapeHtml(label)}${caption ? `<small>${escapeHtml(caption)}</small>` : ""}</span><i>${selected ? icon("check") : ""}</i></button>`;
}

function occupationResults() {
  if (normalizeSearch(occupationQuery)) {
    const matches = searchOccupations(occupationQuery);
    return `<p class="catalog-count" role="status">${matches.length ? `${matches.length} nghề phù hợp` : "Chưa tìm thấy nghề phù hợp. Thử tên khác hoặc chọn Nghề khác bên dưới."}</p><div class="options career-matches" role="radiogroup" aria-label="Kết quả tìm nghề">${matches.map(detail => occupationOption(detail.occupation, detail.label, detail.code, getAnswerLabel("occupation", detail.occupation))).join("")}</div>`;
  }
  const families = occupationFamilies.filter(item => showAllOccupationFamilies || commonOccupationFamilies.includes(item.code) || item.occupations.includes(state.answers.occupation ?? ""));
  return `<p class="catalog-count">Hoặc mở nhóm nghề để chọn. Bạn cũng có thể chỉ chọn nhóm nếu chưa rõ chuyên môn.</p><div class="career-families">${families.map(family => {
    const hasSelected = family.occupations.includes(state.answers.occupation ?? "");
    return `<details class="career-family" ${hasSelected ? "open" : ""}><summary>${escapeHtml(family.label)}<span aria-hidden="true">+</span></summary><div class="career-family-body">${family.occupations.map(code => `<div class="career-subgroup"><h2>${escapeHtml(getAnswerLabel("occupation", code))}</h2><div class="options" role="radiogroup" aria-label="${escapeHtml(getAnswerLabel("occupation", code))}">${searchOccupations("", family.code).filter(detail => detail.occupation === code).map(detail => occupationOption(code, detail.label, detail.code)).join("")}${occupationOption(code, "Chỉ chọn nhóm này — chưa rõ chuyên môn")}</div></div>`).join("")}</div></details>`;
  }).join("")}</div><button type="button" class="catalog-toggle" data-action="toggle-career-families">${showAllOccupationFamilies ? "Thu gọn danh sách nhóm nghề" : `Xem tất cả ${occupationFamilies.length} nhóm nghề`}</button>`;
}

function renderOccupationPicker() {
  const selected = state.answers.occupation;
  return `<div class="career-picker">
    ${selected ? `<div class="career-selection" role="status"><span>Đang chọn</span><strong>${escapeHtml(getOccupationText(state.answers))}</strong><button type="button" data-action="edit-career">Chọn nghề cụ thể / Đổi nghề</button></div>` : ""}
    <label class="catalog-search"><span>Tìm nghề của bạn</span><input id="occupation-search" type="search" autocomplete="off" placeholder="Ví dụ: kế toán, ca sĩ, giáo viên…" value="${escapeHtml(occupationQuery)}" aria-controls="occupation-results"></label>
    <div id="occupation-results">${occupationResults()}</div><div class="options career-fallbacks" role="radiogroup" aria-label="Lựa chọn nghề khác">${occupationOption("OTHER", "Nghề khác — nhập tên nghề")}${occupationOption("UNDECIDED", "Chưa xác định nghề")}</div>
    ${selected === "OTHER" ? `<label class="other-field"><span>Tên nghề của bạn *</span><input id="occupation-other" maxlength="120" placeholder="Nhập tên nghề hoặc công việc chính" value="${escapeHtml(state.answers.occupationOther ?? "")}"></label>` : ""}
  </div>`;
}

function renderSurvey() {
  const list = activeQuestions();
  if (state.step >= list.length) state.step = list.length - 1;
  const question = list[state.step];
  const selected = String(state.answers[question.id] ?? "");
  const overall = Math.round(((state.step + 1) / (list.length + 1)) * 100);
  const otherValue = question.id === "useCase" ? state.answers.useCaseOther : question.id === "blocker" ? state.answers.blockerOther : "";
  return `${renderHeader()}<main id="main" class="survey-shell">
    <div class="survey-top"><button class="back-link" data-action="back">← Quay lại</button><span>Bước ${state.step + 1}/${list.length + 1}</span></div>
    <div class="progress" aria-label="Tiến độ ${overall}%"><i style="width:${overall}%"></i></div>
    <div class="survey-reward">${icon("spark")} Hoàn thành để nhận buổi học miễn phí đúng trình độ cùng chuyên gia</div>
    <section class="question-panel">
      <div class="question-index">${String(state.step + 1).padStart(2,"0")}</div>
      <span class="eyebrow dark">${question.eyebrow}</span>
      <h1>${question.title}</h1><p class="question-hint">${question.hint}</p>
      ${question.id === "occupation" ? renderOccupationPicker() : `${question.id === "industry" ? '<label class="catalog-search"><span>Tìm ngành của bạn</span><input id="industry-search" type="search" autocomplete="off" placeholder="Gõ tên ngành để tìm nhanh"></label>' : ""}<div class="options ${question.id === "industry" ? "catalog-options" : ""}" role="radiogroup" aria-label="${escapeHtml(question.title)}">
        ${question.options.map((option) => `<button class="option ${selected === option.code ? "selected" : ""}" role="radio" aria-checked="${selected === option.code}" data-option="${option.code}"><span>${escapeHtml(option.label)}</span><i>${selected === option.code ? icon("check") : ""}</i></button>`).join("")}
      </div>`}
      ${question.allowOther && selected === "OTHER" ? `<label class="other-field"><span>Mô tả ngắn</span><textarea id="other-answer" maxlength="240" placeholder="Bạn muốn ứng dụng AI vào việc gì?">${escapeHtml(String(otherValue ?? ""))}</textarea></label>` : ""}
      ${state.error ? `<p class="form-error" role="alert">${escapeHtml(state.error)}</p>` : ""}
      <div class="question-actions"><button class="secondary" data-action="back">Quay lại</button><button class="primary" data-action="next" ${selected ? "" : "disabled"}>${question.id === "occupation" ? "Tiếp tục" : "Tiếp tục để nhận buổi học miễn phí"} ${icon("arrow")}</button></div>
    </section>
  </main>${renderFooter()}`;
}

function renderContact() {
  const list = activeQuestions();
  const overall = 100;
  const preview = calculateResult(state.answers as SurveyAnswers);
  return `${renderHeader()}<main id="main" class="survey-shell">
    <div class="survey-top"><button class="back-link" data-action="back-contact">← Quay lại</button><span>Hoàn tất để nhận buổi học miễn phí</span></div>
    <div class="progress"><i style="width:${overall}%"></i></div>
    <section class="contact-panel" data-clarity-mask="true">
      <div class="contact-value"><span>${icon("spark")}</span><div><b>Kết quả của bạn đã sẵn sàng</b><small>Để lại thông tin để nhận kết quả và buổi học miễn phí đúng trình độ cùng chuyên gia.</small></div></div>
      <span class="eyebrow dark">QUYỀN LỢI DÀNH CHO BẠN</span><h1>Nhận buổi học miễn phí đúng trình độ cùng chuyên gia</h1>
      <p class="contact-lead">FUNiX dựa trên kết quả vừa hoàn thành để xác định bạn đang ở đâu, muốn đi tới đâu và nên bắt đầu với lớp nào.</p>
      <div class="course-preview"><small>LỚP BẮT ĐẦU ĐỀ XUẤT</small><b>${preview.recommendedCourseTitle}</b></div>
      <div class="contact-benefits"><span>${icon("check")} Học đúng level, không phải bắt đầu lại từ đầu</span><span>${icon("check")} Thực hành trên bài toán công việc thực tế</span><span>${icon("check")} Mentor công nghệ hướng dẫn trong quá trình học</span><span>${icon("check")} Có lộ trình tiếp theo và cam kết đầu ra rõ ràng</span><span>${icon("check")} Học lại miễn phí đến khi thành thạo</span><span>${icon("check")} Hoàn tiền nếu không đạt đầu ra theo cam kết*</span></div>
      <form id="contact-form" novalidate>
        <label><span>Họ và tên *</span><input name="name" autocomplete="name" maxlength="80" placeholder="Nguyễn Văn A" value="${escapeHtml(state.contact.name)}" required></label>
        <div class="field-row"><label><span>Số điện thoại *</span><input name="phone" autocomplete="tel" inputmode="tel" maxlength="20" placeholder="09xx xxx xxx" value="${escapeHtml(state.contact.phone)}" required></label><label><span>Email *</span><input name="email" type="email" autocomplete="email" maxlength="120" placeholder="email@example.com" value="${escapeHtml(state.contact.email)}" required></label></div>
        <label class="checkbox"><input name="consent" type="checkbox" ${state.contact.consent ? "checked" : ""}><span>Thông tin tôi chia sẻ sẽ được FUNiX sử dụng để tìm hiểu nhu cầu và xây dựng các chương trình học phù hợp hơn với người học.</span></label>
        ${state.error ? `<p class="form-error" role="alert">${escapeHtml(state.error)}</p>` : ""}
        <button class="primary submit" type="submit" ${state.submitting ? "disabled" : ""}>${state.submitting ? "Đang tạo định hướng…" : `NHẬN BUỔI HỌC MIỄN PHÍ ĐÚNG TRÌNH ĐỘ ${icon("arrow")}`}</button>
        <p class="offer-note">*Áp dụng theo điều kiện và chính sách của chương trình.</p>
        <p class="privacy-note">Thông tin của bạn được dùng cho tư vấn FUNiX và không được gửi qua Pixel/Clarity.</p>
      </form>
    </section>
  </main>${renderFooter()}`;
}

function renderResult() {
  const result = state.result!;
  const course = courses.find((item) => item.id === result.recommendedCourseId)!;
  return `${renderHeader()}<main id="main" class="result-page">
    <section class="result-hero"><span class="result-check">${icon("check")}</span><span class="eyebrow">ĐỊNH HƯỚNG DÀNH CHO BẠN</span><h1>Điểm bắt đầu phù hợp đã sẵn sàng.</h1><p>Dựa trên việc bạn đã làm và bằng chứng đang có.</p></section>
    <section class="result-grid">
      <article class="result-card target"><span>${icon("target")}</span><small>ĐÍCH BẠN MUỐN TỚI</small><h2>${escapeHtml(result.targetSummary)}</h2></article>
      <article class="result-card"><small>HIỆN TẠI</small><h3>${escapeHtml(result.currentSummary)}</h3>${result.needsReview ? '<p class="review-flag">Một số tín hiệu chưa đồng nhất; FUNiX sẽ hỏi thêm khi tư vấn.</p>' : ""}</article>
    </section>
    <section class="recommendation">
      <div class="course-code"><small>ĐIỂM BẮT ĐẦU ĐỀ XUẤT</small><strong>${course.id}</strong><span>LỚP ${course.classNumber}</span></div>
      <div><span class="eyebrow dark">BUỔI HỌC PHÙ HỢP CÙNG CHUYÊN GIA</span><h2>${escapeHtml(course.title)}</h2><p>${escapeHtml(course.problem)}</p><div class="unlock"><b>${icon("spark")} Sau khi hoàn thành</b><span>${escapeHtml(course.outcome)}</span></div></div>
    </section>
    <section class="next-step"><div><span class="eyebrow dark">BƯỚC CẦN LÀM TRƯỚC</span><h2>${escapeHtml(result.nextStep)}</h2></div></section>
    <section class="roadmap"><span class="eyebrow dark">LỘ TRÌNH TỚI MỤC TIÊU</span><div class="roadmap-line">${result.roadmap.map((item,index)=>`<div class="roadmap-step ${index===0?"active":""}"><i>${index+1}</i><span>${escapeHtml(item)}</span></div>`).join('<b aria-hidden="true">→</b>')}</div></section>
    <section class="zalo-community" aria-labelledby="zalo-community-title">
      <div class="zalo-community-copy">
        <span class="eyebrow dark">CỘNG ĐỒNG THỰC CHIẾN AI</span>
        <h2 id="zalo-community-title">Vào nhóm Zalo để học và thực hành cùng Mentor FUNiX</h2>
        <p>Quét mã QR hoặc nhấn nút bên dưới để tham gia nhóm, nhận thông báo về lớp học, workshop và trao đổi cùng cộng đồng ứng dụng AI.</p>
        <ul><li>Nhận thông báo chương trình mới nhất</li><li>Trao đổi với Mentor và người học AI</li><li>Cập nhật tài liệu, bài thực hành và hoạt động cộng đồng</li></ul>
        <a class="primary large zalo-join" data-action="join-zalo" href="https://zalo.me/g/ccxxjqnj8er0eqvo4txi" target="_blank" rel="noopener noreferrer">THAM GIA NHÓM ZALO ${icon("arrow")}</a>
      </div>
      <a class="zalo-qr" data-action="join-zalo" href="https://zalo.me/g/ccxxjqnj8er0eqvo4txi" target="_blank" rel="noopener noreferrer" aria-label="Mở nhóm Zalo Thực chiến AI cùng Mentor FUNiX">
        <img src="/zalo-group-qr.png" alt="Mã QR tham gia nhóm Zalo Thực chiến AI cùng Mentor FUNiX">
        <span>Quét QR bằng Zalo để tham gia</span>
      </a>
    </section>
    <section class="result-cta"><div><h2>FUNiX sẽ liên hệ để giúp bạn bắt đầu đúng bước.</h2><p>Thông tin đã được ghi nhận. Bạn có thể lưu lại màn hình này để trao đổi cùng tư vấn viên.</p></div><button class="secondary light" data-action="restart">Làm lại khảo sát</button></section>
  </main>${renderFooter()}`;
}

function renderFooter() { return `<footer><div class="brand inverse"><img class="brand-logo" src="/funix-logo.png" alt="FUNiX — Learn with Mentors"></div><p>© 2026 FUNiX · Khảo sát nhu cầu ứng dụng AI</p></footer>`; }

function render() {
  app.innerHTML = state.view === "landing" ? renderLanding() : state.view === "survey" ? renderSurvey() : state.view === "contact" ? renderContact() : renderResult();
  bindEvents();
  if (state.view === "survey" || state.view === "contact" || state.view === "result") document.querySelector("h1")?.focus?.();
}

function startSurvey() { state.view = "survey"; state.step = 0; state.error = ""; persist(); track("survey_start"); render(); window.scrollTo({ top: 0, behavior: "smooth" }); }

function bindOccupationResults(root: Document | Element = document) {
  root.querySelectorAll<HTMLButtonElement>("[data-career]").forEach(el => el.addEventListener("click", () => {
    state.answers.occupation = el.dataset.career!;
    state.answers.occupationDetail = el.dataset.careerDetail || undefined;
    state.answers.occupationOther = undefined;
    state.error = "";
    persist();
    // Detailed job titles and user-entered text stay out of advertising telemetry.
    track("survey_question_answer", { question: "occupation" });
    render();
  }));
  root.querySelector<HTMLButtonElement>("[data-action='toggle-career-families']")?.addEventListener("click", () => {
    showAllOccupationFamilies = !showAllOccupationFamilies;
    document.querySelector("#occupation-results")!.innerHTML = occupationResults();
    bindOccupationResults(document.querySelector("#occupation-results")!);
  });
}

function bindEvents() {
  if (state.view === "survey" && activeQuestions()[state.step].id === "occupation") {
    bindOccupationResults();
    document.querySelector<HTMLInputElement>("#occupation-search")?.addEventListener("input", event => {
      occupationQuery = (event.target as HTMLInputElement).value;
      document.querySelector("#occupation-results")!.innerHTML = occupationResults();
      bindOccupationResults(document.querySelector("#occupation-results")!);
    });
    document.querySelector<HTMLInputElement>("#occupation-other")?.addEventListener("input", event => {
      state.answers.occupationOther = (event.target as HTMLInputElement).value;
      persist();
    });
    document.querySelector<HTMLButtonElement>("[data-action='edit-career']")?.addEventListener("click", () => {
      occupationQuery = "";
      state.error = "";
      render(); document.querySelector<HTMLInputElement>("#occupation-search")?.focus();
    });
  }
  document.querySelector<HTMLInputElement>("#industry-search")?.addEventListener("input", event => {
    const words = normalizeSearch((event.target as HTMLInputElement).value).split(/\s+/).filter(Boolean);
    document.querySelectorAll<HTMLButtonElement>("[data-option]").forEach(button => {
      const label = normalizeSearch(button.textContent ?? "");
      button.hidden = !["OTHER", "UNDECIDED"].includes(button.dataset.option ?? "") && !words.every(word => label.includes(word));
    });
  });
  document.querySelectorAll<HTMLElement>("[data-action='start']").forEach((el) => el.addEventListener("click", startSurvey));
  document.querySelector<HTMLElement>("[data-action='home']")?.addEventListener("click", (event) => { event.preventDefault(); state.view = "landing"; render(); });
  document.querySelectorAll<HTMLButtonElement>("[data-option]").forEach((el) => el.addEventListener("click", () => {
    const q = activeQuestions()[state.step];
    if (q.id === "workStatus" && state.answers.workStatus !== el.dataset.option) {
      state.answers.industry = undefined;
      state.answers.occupation = undefined;
      state.answers.occupationDetail = undefined;
      state.answers.occupationOther = undefined;
      occupationQuery = "";
    }
    (state.answers as Record<string,string>)[q.id] = el.dataset.option!;
    if (q.id === "action" || q.id === "evidence") state.answers.advanced = undefined;
    state.error = ""; persist(); track("survey_question_answer", { question: q.id, answer: el.dataset.option! }); render();
  }));
  document.querySelector<HTMLElement>("[data-action='next']")?.addEventListener("click", () => {
    const list = activeQuestions(); const q = list[state.step]; const value = String(state.answers[q.id] ?? "");
    const other = document.querySelector<HTMLTextAreaElement>("#other-answer")?.value.trim();
    if (!value) return;
    if (q.id === "occupation" && value === "OTHER" && !state.answers.occupationOther?.trim()) { state.error = "Hãy nhập tên nghề hoặc công việc chính của bạn."; render(); return; }
    if (q.allowOther && value === "OTHER" && !other) { state.error = "Hãy mô tả ngắn câu trả lời của bạn."; render(); return; }
    if (q.id === "useCase" && other) state.answers.useCaseOther = other;
    if (q.id === "blocker" && other) state.answers.blockerOther = other;
    track("survey_question_complete", q.id === "occupation" ? { question: q.id } : { question: q.id, answer: value });
    occupationQuery = "";
    if (state.step < list.length - 1) state.step += 1; else state.view = "contact";
    state.error = ""; persist(); render(); window.scrollTo({ top: 0, behavior: "smooth" });
  });
  document.querySelectorAll<HTMLElement>("[data-action='back']").forEach((el) => el.addEventListener("click", () => { if (state.step > 0) state.step -= 1; else state.view = "landing"; state.error=""; persist(); render(); }));
  document.querySelector<HTMLElement>("[data-action='back-contact']")?.addEventListener("click", () => { state.view = "survey"; state.step = activeQuestions().length - 1; persist(); render(); });
  document.querySelector<HTMLElement>("[data-action='restart']")?.addEventListener("click", () => { state.view="landing"; state.step=0; state.answers={}; state.contact={name:"",phone:"",email:"",consent:true}; state.result=undefined; render(); window.scrollTo({top:0}); });
  document.querySelectorAll<HTMLElement>("[data-action='join-zalo']").forEach((el) => el.addEventListener("click", () => track("zalo_group_click", { course: state.result?.recommendedCourseId ?? "" })));
  document.querySelector<HTMLFormElement>("#contact-form")?.addEventListener("submit", submitForm);
}

function normalizePhone(value: string) { const clean = value.replace(/[^\d+]/g, ""); return clean.startsWith("+84") ? `0${clean.slice(3)}` : clean; }

async function submitForm(event: SubmitEvent) {
  event.preventDefault();
  const form = new FormData(event.currentTarget as HTMLFormElement);
  state.contact = { name: String(form.get("name") ?? "").trim(), phone: normalizePhone(String(form.get("phone") ?? "")), email: String(form.get("email") ?? "").trim().toLowerCase(), consent: form.get("consent") === "on" };
  if (state.contact.name.length < 2) state.error = "Vui lòng nhập họ và tên.";
  else if (!/^(0)(3|5|7|8|9)\d{8}$/.test(state.contact.phone)) state.error = "Số điện thoại Việt Nam chưa đúng định dạng.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.contact.email)) state.error = "Email chưa đúng định dạng.";
  else if (!state.contact.consent) state.error = "Bạn cần đồng ý để FUNiX lưu thông tin và liên hệ tư vấn.";
  else state.error = "";
  if (state.error) { render(); return; }

  localStorage.setItem(trackingConsentKey, "granted");
  loadTracking();
  state.submitting = true; render();
  const submissionId = crypto.randomUUID();
  const params = new URLSearchParams(location.search);
  const payload = {
    contact: state.contact,
    answers: state.answers,
    attribution: { sourceUrl: location.href, utmSource: params.get("utm_source") ?? "", utmMedium: params.get("utm_medium") ?? "", utmCampaign: params.get("utm_campaign") ?? "", utmContent: params.get("utm_content") ?? "", device: matchMedia("(max-width: 720px)").matches ? "Mobile" : "Desktop" },
    meta: { submissionId, surveyVersion: SURVEY_VERSION, ruleVersion: RULE_VERSION, consentVersion: CONSENT_VERSION, clientTimestamp: new Date().toISOString() },
  };
  try {
    const response = await fetch("/api/submissions", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(payload) });
    const data = await response.json() as { ok: boolean; result?: SurveyResult; error?: string; submissionId?: string };
    if (!response.ok || !data.ok || !data.result) throw new Error(data.error || "Không thể lưu khảo sát");
    state.result = data.result; state.view = "result"; state.submitting = false; sessionStorage.removeItem(storageKey); track("lead_submit_success", { submissionId: data.submissionId ?? submissionId, course: data.result.recommendedCourseId }); render(); track("result_view", { course: data.result.recommendedCourseId }); window.scrollTo({top:0,behavior:"smooth"});
  } catch {
    state.submitting = false; state.error = "Kết nối chưa ổn định. Câu trả lời vẫn được giữ, vui lòng thử lại."; render();
  }
}

function loadTracking() {
  const clarityId = import.meta.env.VITE_CLARITY_PROJECT_ID as string | undefined;
  const pixelId = import.meta.env.VITE_META_PIXEL_ID as string | undefined;
  if (clarityId && !document.querySelector("script[data-clarity]")) {
    const w = window as typeof window & { clarity?: (...args: unknown[]) => void };
    const queue = (...args: unknown[]) => ((queue as unknown as { q: unknown[][] }).q ||= []).push(args);
    w.clarity = queue; const s = document.createElement("script"); s.async = true; s.dataset.clarity="true"; s.src=`https://www.clarity.ms/tag/${clarityId}`; document.head.appendChild(s);
  }
  if (pixelId && !document.querySelector("script[data-meta-pixel]")) {
    const w = window as typeof window & { fbq?: (...args: unknown[]) => void };
    const fbq = (...args: unknown[]) => ((fbq as unknown as { queue: unknown[][] }).queue ||= []).push(args);
    w.fbq = fbq; const s=document.createElement("script"); s.async=true; s.dataset.metaPixel="true"; s.src="https://connect.facebook.net/en_US/fbevents.js"; document.head.appendChild(s); fbq("init",pixelId); fbq("track","PageView");
  }
}

if (localStorage.getItem(trackingConsentKey) === "granted") loadTracking();
render();
